import { DOCUMENT } from '@angular/common';
import { Injectable, NgZone, inject, signal } from '@angular/core';
import { SwUpdate, VersionReadyEvent } from '@angular/service-worker';
import { filter } from 'rxjs';

/** Cada cuánto se pregunta por una versión nueva con la pestaña visible */
const CHECK_INTERVAL_MS = 15 * 60 * 1000;

/**
 * Avisa cuando hay una versión nueva del admin publicada.
 *
 * El service worker de Angular descarga la versión nueva en segundo plano
 * (cada build del CI genera un `ngsw.json` con hash distinto), pero sigue
 * sirviendo la vieja hasta una recarga completa. Sin este aviso, los usuarios
 * se quedaban en un release anterior y recargaban a ciegas.
 *
 * A diferencia del portal de clientes, aquí la versión **no se aplica sola**:
 * recargar sin avisar puede tirar una captura a medio llenar. Se marca
 * `updateReady` y el usuario decide cuándo actualizar.
 *
 * Sin service worker (desarrollo, navegadores sin soporte, o recién hecho un
 * Ctrl + Shift + R, que ya trae la última versión) no hace nada.
 */
@Injectable({ providedIn: 'root' })
export class AppUpdateService {
  private readonly swUpdate = inject(SwUpdate);
  private readonly zone = inject(NgZone);
  private readonly document = inject(DOCUMENT);

  /** Hay una versión descargada esperando a que el usuario actualice */
  public readonly updateReady = signal(false);

  /** Ya se pidió actualizar; evita activar dos veces con doble clic */
  public readonly applying = signal(false);

  private started = false;

  /** Arranca la vigilancia de versiones; llamarlo una vez al iniciar la app */
  public init(): void {
    if (this.started || !this.swUpdate.isEnabled) {
      return;
    }

    this.started = true;

    this.swUpdate.versionUpdates
      .pipe(filter((event): event is VersionReadyEvent => event.type === 'VERSION_READY'))
      .subscribe((event) => {
        console.info(
          `🔄 [AppUpdate] Versión nueva lista (${event.latestVersion.hash.slice(0, 8)}); se avisa al usuario.`,
        );
        this.updateReady.set(true);
      });

    // Los archivos de la versión en uso ya no existen en el servidor (el deploy
    // los borra): la app quedaría rota, así que se recarga sin esperar.
    this.swUpdate.unrecoverable.subscribe((event) => {
      console.error('❌ [AppUpdate] Estado irrecuperable del service worker:', event.reason);
      this.document.location.reload();
    });

    this.zone.runOutsideAngular(() => {
      this.document.addEventListener('visibilitychange', () => {
        if (this.document.visibilityState === 'visible' && !this.updateReady()) {
          void this.check();
        }
      });

      setInterval(() => {
        if (this.document.visibilityState === 'visible' && !this.updateReady()) {
          void this.check();
        }
      }, CHECK_INTERVAL_MS);
    });

    void this.check();
  }

  /** Activa la versión descargada y recarga la página actual */
  public async apply(): Promise<void> {
    if (this.applying()) {
      return;
    }

    this.applying.set(true);

    try {
      await this.swUpdate.activateUpdate();
    } catch (error: unknown) {
      console.warn('⚠️ [AppUpdate] No se pudo activar la versión nueva; se recarga igual:', error);
    }

    this.document.location.reload();
  }

  /** Pregunta al servidor si hay versión nueva; un fallo de red no importa */
  private async check(): Promise<void> {
    try {
      await this.swUpdate.checkForUpdate();
    } catch (error: unknown) {
      console.warn('⚠️ [AppUpdate] No se pudo buscar versión nueva:', error);
    }
  }
}
